'use client';

import React, { useState, useEffect } from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography'
import { Button, Card, Alert, Spin, Space, Divider } from 'antd';
import { authService } from '@/domains/auth/services/auth-service';

const { Title, Text, Paragraph } = Typography;

export const ConnectionDiagnostics: React.FC = () => {
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Implement getConnectionDiagnostics method in AuthService
      const results = {
        apiUrl: process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_API_URL || '(same-origin BFF)',
        backendStatus: 'reachable',
        frontendStatus: 'valid',
        sslCertificate: 'valid',
        recommendations: []
      }; // await authService.getConnectionDiagnostics();
      setDiagnostics(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Diagnostics failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reachable':
      case 'valid':
        return 'success';
      case 'ssl_error':
      case 'self_signed':
        return 'warning';
      case 'unreachable':
      case 'invalid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'reachable':
        return '✅ Reachable';
      case 'unreachable':
        return '❌ Unreachable';
      case 'ssl_error':
        return '⚠️ SSL Error';
      case 'valid':
        return '✅ Valid';
      case 'self_signed':
        return '⚠️ Self-Signed';
      case 'invalid':
        return '❌ Invalid';
      default:
        return '❓ Unknown';
    }
  };

  return (
    <Card title="Connection Diagnostics" style={{ maxWidth: 800, margin: '20px auto' }}>
      <Space orientation="vertical" style={{ width: '100%' }}>
        <div>
          <Button
            type="primary"
            onClick={runDiagnostics}
            loading={loading}
            style={{ marginBottom: 16 }}
          >
            Run Diagnostics
          </Button>
        </div>

        {error && (
          <Alert
            title="Diagnostics Error"
            description={error}
            type="error"
            showIcon
          />
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Running connection diagnostics...</Text>
            </div>
          </div>
        )}

        {diagnostics && (
          <div>
            <Title level={4}>Connection Status</Title>

            <Space orientation="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>API URL: </Text>
                <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>{diagnostics.apiUrl}</code>
              </div>

              <div>
                <Text strong>Backend Status: </Text>
                <Alert
                  title={getStatusText(diagnostics.backendStatus)}
                  type={getStatusColor(diagnostics.backendStatus) as any}
                  showIcon={false}
                  style={{ display: 'inline-block', marginLeft: 8 }}
                />
              </div>

              <div>
                <Text strong>Frontend Status: </Text>
                <Alert
                  title={getStatusText(diagnostics.frontendStatus)}
                  type={getStatusColor(diagnostics.frontendStatus) as any}
                  showIcon={false}
                  style={{ display: 'inline-block', marginLeft: 8 }}
                />
              </div>

              <div>
                <Text strong>SSL Certificate: </Text>
                <Alert
                  title={getStatusText(diagnostics.sslCertificate)}
                  type={getStatusColor(diagnostics.sslCertificate) as any}
                  showIcon={false}
                  style={{ display: 'inline-block', marginLeft: 8 }}
                />
              </div>
            </Space>

            {diagnostics.recommendations && diagnostics.recommendations.length > 0 && (
              <>
                <Divider />
                <Title level={4}>Recommendations</Title>
                <Alert
                  title="Troubleshooting Steps"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {diagnostics.recommendations?.map((rec: string, index: number) => (
                        <li key={index}>
                          <Text>{rec}</Text>
                        </li>
                      ))}
                    </ul>
                  }
                  type="info"
                  showIcon
                />
              </>
            )}

            {diagnostics.sslCertificate === 'self_signed' && (
              <>
                <Divider />
                <Alert
                  title="SSL Certificate Solution"
                  description={
                    <div>
                      <Paragraph>
                        The server is using a self-signed certificate for development. To fix this:
                      </Paragraph>
                      <ol>
                        <li>
                          <Text strong>Visit the server directly:</Text>
                          <br />
                          <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>https://192.168.56.130</code>
                        </li>
                        <li>
                          <Text strong>Accept the security warning</Text> in your browser
                        </li>
                        <li>
                          <Text strong>Refresh this page</Text> and run diagnostics again
                        </li>
                      </ol>
                      <Paragraph>
                        <Text type="secondary">
                          Note: This is normal for development environments with self-signed certificates.
                        </Text>
                      </Paragraph>
                    </div>
                  }
                  type="warning"
                  showIcon
                />
              </>
            )}
          </div>
        )}
      </Space>
    </Card>
  );
};
